/**
 * MultiHierarchyHint — Spec 287: one-time dismissible card teaching the
 * header Hierarchy chip, shown the first time a user's Hierarchy count is
 * 2+. Mirrors PillarTooltip's (Spec 163) localStorage-seen-state + dismiss
 * pattern exactly, keyed separately so the two features don't share state.
 *
 * Renders below the header, mobile-only (<768px) — the chip it points at
 * only exists at that breakpoint.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { useOrganization } from '../../contexts/OrganizationContext';

const STORAGE_KEY = 'helm_multi_hierarchy_hint_seen';

function isHintSeen(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    // Storage unavailable/corrupt — degrade to "seen" so a broken flag can't
    // re-show the hint on every render (Spec 287 edge case: must not crash
    // or loop when storage is unavailable).
    return true;
  }
}

function markHintSeen(): void {
  try {
    localStorage.setItem(STORAGE_KEY, '1');
  } catch {
    // Silent fail — mirrors PillarTooltip: hint may re-appear next session,
    // which is an acceptable degradation, not a crash.
  }
}

const MultiHierarchyHint: React.FC = () => {
  const { organizations } = useOrganization();
  const [dismissed, setDismissed] = useState(false);

  const shouldShow = !dismissed && organizations.length >= 2 && !isHintSeen();

  const handleGotIt = useCallback(() => {
    markHintSeen();
    setDismissed(true);
  }, []);

  // Escape key dismisses, mirroring PillarTooltip.
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && shouldShow) handleGotIt();
    },
    [shouldShow, handleGotIt]
  );

  useEffect(() => {
    if (!shouldShow) return;
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [shouldShow, handleKeyDown]);

  if (!shouldShow) return null;

  // Computed only once we know the card will actually render -- this
  // component mounts app-wide on every authenticated page (via
  // PillarLayout), so calling window.matchMedia() unconditionally on every
  // render/navigation is unnecessary (helm-skeptical-evaluator finding).
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return (
    <div
      className={`w-full flex justify-center px-3 md:hidden ${
        prefersReducedMotion ? '' : 'animate-pillar-tooltip-slide-down'
      }`}
      data-testid="multi-hierarchy-hint"
      role="status"
      aria-label="About switching Hierarchies"
    >
      <Card
        tone="elevated"
        variant="bordered"
        borderColor="medium"
        radius="lg"
        padding="md"
        className="w-full max-w-[400px] my-3 shadow-sm"
      >
        <p className="m-0 mb-1 text-base font-semibold text-[var(--theme-text-primary)]">
          You&apos;re in two Hierarchies now
        </p>
        <p className="m-0 mb-3 text-sm text-text-secondary">
          Tap the name above to switch anytime.
        </p>
        <Button variant="primary" size="sm" onClick={handleGotIt}>
          Got it
        </Button>
      </Card>
    </div>
  );
};

export default MultiHierarchyHint;
